import BaseApiService from './BaseApiService'

/** A custom role within the team, as returned by roles/list and roles/create. */
export interface Role {
  roleId: string
  teamId: string
  name: string
  createdAt: string
  createdBy: string
  memberCount: number
}

/** A user who belongs to a role (roles/members). */
export interface RoleMember {
  userId: string
  emailAddress: string
  firstName: string | null
  lastName: string | null
}

/** A role's grant on a folder (roles/folder-grants). */
export interface FolderGrant {
  folderRoleGrantId: string
  folderId: string
  roleId: string
  roleName: string
  permissionActionLookupId: number
}

/**
 * Folder-RBAC management. Roles and membership are team-admin only; folder grants require
 * MANAGE on the folder (the API enforces both, so the UI only soft-gates the nav link).
 */
class RolesApiService extends BaseApiService {
  list(): Promise<Role[]> {
    return this.post<Role[]>('/v1/roles/list', {})
  }

  create(name: string): Promise<Role> {
    return this.post<Role>('/v1/roles/create', { name })
  }

  delete(roleId: string): Promise<void> {
    return this.post<void>('/v1/roles/delete', { roleId })
  }

  members(roleId: string): Promise<RoleMember[]> {
    return this.post<RoleMember[]>('/v1/roles/members', { roleId })
  }

  assign(userId: string, roleId: string): Promise<void> {
    return this.post<void>('/v1/roles/assign', { userId, roleId })
  }

  unassign(userId: string, roleId: string): Promise<void> {
    return this.post<void>('/v1/roles/unassign', { userId, roleId })
  }

  folderGrants(folderId: string): Promise<FolderGrant[]> {
    return this.post<FolderGrant[]>('/v1/roles/folder-grants', { folderId })
  }

  grant(folderId: string, roleId: string, action: number): Promise<void> {
    return this.post<void>('/v1/roles/grant', { folderId, roleId, action })
  }

  revoke(folderId: string, roleId: string, action: number): Promise<void> {
    return this.post<void>('/v1/roles/revoke', { folderId, roleId, action })
  }

  /** All team capability grants (role_id + capability). */
  capabilities(): Promise<CapabilityGrant[]> {
    return this.post<CapabilityGrant[]>('/v1/roles/capabilities', {})
  }

  grantCapability(roleId: string, capability: string): Promise<void> {
    return this.post<void>('/v1/roles/grant-capability', { roleId, capability })
  }

  revokeCapability(roleId: string, capability: string): Promise<void> {
    return this.post<void>('/v1/roles/revoke-capability', { roleId, capability })
  }
}

export interface CapabilityGrant {
  teamCapabilityGrantId: string
  roleId: string
  capability: string
}

/** Capability codes (mirror lib TeamCapability) with display labels. */
export const TEAM_CAPABILITIES: { code: string; label: string }[] = [
  { code: 'manage_members', label: 'Manage members' },
  { code: 'manage_billing', label: 'Manage billing' },
  { code: 'manage_plan', label: 'Manage plan' },
  { code: 'manage_api_keys', label: 'Manage API keys' },
  { code: 'manage_connectors', label: 'Manage connectors' },
  { code: 'manage_templates', label: 'Manage templates' },
  { code: 'manage_folder_permissions', label: 'Manage folder permissions' },
]

export const rolesApiService = new RolesApiService()
